import { graphql } from "@acto/gql";
import type { DateRangeInput } from "@acto/gql/graphql";
import { useMemo, useCallback } from "react";
import { useQuery } from "urql";
import { useComparisonModeContext } from "@acto/taskpane/core/contexts/TenantSettingsContext";
import { toGqlKpiComparisonMode } from "@acto/taskpane/core/data-access/customers/kpiTransformers";
import { Workflow, WorkflowActivity } from "@acto/taskpane/core/domain";
import type { WorkflowMetrics } from "./types";

const WorkflowByIdQuery = graphql(`
  query GetWorkflowById($id: ID!, $dateRange: DateRangeInput!, $comparisonMode: KpiComparisonMode!) {
    workflow(id: $id) {
      id
      name
      description
      isActive
      isCustomQuery
      repeat
      dueDate
      pauseTill
      lastChange
      activeTodosCount
      fixedResponsibleEmployee {
        id
        name
      }
      query {
        groupConjunction
        groups {
          conjunction
          conditions {
            filter
            params {
              name
              operator
              stringValue
              stringValues
              numericValue
              intValue
              dateValue
              boolValue
            }
          }
        }
      }
      kpis(dateRange: $dateRange, comparisonMode: $comparisonMode) {
        todosCreated
        todosCreatedChange
        todosCompleted
        todosCompletedChange
        todosDeleted
        todosDeletedChange
        overdueTodos
        overdueTodosChange
      }
      todos(first: 20) {
        edges {
          node {
            id
            createdAt
            processedStatus
            assignedEmployee {
              id
              name
            }
            client {
              id
              name
              externalReference
            }
          }
        }
      }
    }
  }
`);

interface UseWorkflowByIdResult {
  workflow: Workflow | null;
  metrics: WorkflowMetrics | null;
  activities: WorkflowActivity[];
  isPending: boolean;
  isFetching: boolean;
  refetch?: () => void;
}

export function useWorkflowById(workflowId: string | null, dateRange: DateRangeInput): UseWorkflowByIdResult {
  const comparisonMode = toGqlKpiComparisonMode(useComparisonModeContext());
  const [result, reexecuteQuery] = useQuery({
    query: WorkflowByIdQuery,
    variables: { id: workflowId ?? "", dateRange, comparisonMode },
    pause: !workflowId,
    requestPolicy: "cache-and-network",
  });

  const { data, fetching: isFetching } = result;
  const rawWorkflow = data?.workflow;

  const isPending = isFetching && !data;

  const refetch = useCallback(() => {
    reexecuteQuery({ requestPolicy: "network-only" });
  }, [reexecuteQuery]);

  const workflow = useMemo(() => (rawWorkflow ? Workflow.fromGraphql(rawWorkflow) : null), [rawWorkflow]);

  const metrics = useMemo<WorkflowMetrics | null>(() => {
    const kpis = rawWorkflow?.kpis;
    if (!kpis) return null;

    return {
      created: kpis.todosCreated,
      createdChange: kpis.todosCreated === 0 ? null : (kpis.todosCreatedChange ?? null),
      deleted: kpis.todosDeleted,
      deletedChange: kpis.todosDeleted === 0 ? null : (kpis.todosDeletedChange ?? null),
      completed: kpis.todosCompleted,
      completedChange: kpis.todosCompleted === 0 ? null : (kpis.todosCompletedChange ?? null),
      overdue: kpis.overdueTodos,
      overdueChange: kpis.overdueTodos === 0 ? null : (kpis.overdueTodosChange ?? null),
    };
  }, [rawWorkflow?.kpis]);

  const activities = useMemo<WorkflowActivity[]>(() => {
    if (!rawWorkflow?.todos?.edges) return [];

    return rawWorkflow.todos.edges.map((edge) => WorkflowActivity.fromGraphql(edge.node));
  }, [rawWorkflow?.todos?.edges]);

  return {
    workflow,
    metrics,
    activities,
    isPending,
    isFetching,
    refetch,
  };
}
